import React, { createContext, useState, useContext, type ReactNode, useEffect } from "react";

import * as Sentry from "@sentry/react";

import { ALL_ROLES } from "@/constants/pagePermissionDefaults";
import { logger } from "@/utils/logger";
import {
  DEFAULT_TIMEZONE_SETTINGS,
  mapLocaleSettingsToDisplay,
  mapLocaleSettingsToEnum,
} from "@/utils/timeParser";

import { type LoginResponse, type AuthContextType } from "../interface/Auth";
import { loginFunction, checkAuthFunction, initiateMicrosoftLogin } from "../services/auth";
import { getUserOrganization } from "../services/roles";
import { getUserLocalePreferences, upsertUserLocalePreferences } from "../services/userLocalePreferences";

export const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(false);
  const [username, setUsername] = useState<string>("");
  const [role, setRole] = useState<string>("");
  const [loading, setLoading] = useState<boolean>(true);
  const [errorMessage, setErrorMessage] = useState<string>("");
  const [hasSeenWelcomePopup, setHasSeenWelcomePopup] = useState<boolean>(false);
  const [isNewLogin, setIsNewLogin] = useState<boolean>(false);

  // NEW: 2FA state
  const [requires2FA, setRequires2FA] = useState<boolean>(false);
  const [pending2FAEmail, setPending2FAEmail] = useState<string>("");

  const syncUserLocalePreferences = async () => {
    try {
      const response = await getUserLocalePreferences();

      if (response) {
        const mappedSettings = mapLocaleSettingsToDisplay(response);
        localStorage.setItem(
          "timezoneSettings",
          JSON.stringify({ ...DEFAULT_TIMEZONE_SETTINGS, ...mappedSettings })
        );
        return;
      }

      // Backend has no locale record: backfill from localStorage or defaults.
      let localSettings = { ...DEFAULT_TIMEZONE_SETTINGS };
      try {
        const raw = localStorage.getItem("timezoneSettings");
        if (raw) {
          const parsed = JSON.parse(raw) as {
            timezone?: string;
            dateFormat?: string;
            timeFormat?: string;
          };
          localSettings = mapLocaleSettingsToDisplay(parsed);
        }
      } catch {
        localSettings = { ...DEFAULT_TIMEZONE_SETTINGS };
      }

      localStorage.setItem("timezoneSettings", JSON.stringify(localSettings));
      await upsertUserLocalePreferences(mapLocaleSettingsToEnum(localSettings));
    } catch (error) {
      logger.warn("Unable to sync locale preferences on login", {
        error,
        context: "syncUserLocalePreferences",
      });
    }
  };

  useEffect(() => {
    const publicAuthPaths = ["/forgot-password", "/reset-password", "/set-password"];
    const isOnPublicAuthPage = publicAuthPaths.some((p) =>
      window.location.pathname.startsWith(p)
    );

    if (isOnPublicAuthPage) {
      setIsAuthenticated(false);
      setLoading(false);
      return;
    }

    const verifySession = async () => {
      try {
        setLoading(true);
        const response = await checkAuthFunction();
        if (response.isAuthenticated === true) {
          const success = await fetchUserInfo();
          if (!success) {
            setIsAuthenticated(false);
          }
        } else {
          setIsAuthenticated(false);
        }
      } catch (error) {
        logger.error("Session verification error", { error, context: "verifySession" });
        setIsAuthenticated(false);
      } finally {
        setLoading(false);
      }
    };

    verifySession();
  }, []);

  const login = async (
    email: string,
    password: string
  ): Promise<{ success: boolean; requires2FA?: boolean }> => {
    try {
      setLoading(true);
      setErrorMessage(""); // Clear any previous error messages
      const response: LoginResponse | null = await loginFunction(email, password);

      // Check if 2FA is required
      if (response?.requires2FA === true) {
        setRequires2FA(true);
        setPending2FAEmail(email);
        return { success: true, requires2FA: true };
      }

      if (response?.message?.toLowerCase() === "login successful") {
        // Fetch user info and validate role
        const userInfo = await fetchUserInfo();
        if (!userInfo) {
          setErrorMessage("Invalid credentials. Please check your email and password.");
          return { success: false };
        }
        // Mark this as a new login session
        setIsNewLogin(true);
        return { success: true, requires2FA: false };
      }
      setErrorMessage(response?.message ?? "Login failed.");
      return { success: false };
    } catch (error) {
      logger.error("Login error", { error, context: "login" });
      setErrorMessage(
        error instanceof Error ? error.message : "An error occurred while logging in."
      );
      return { success: false };
    } finally {
      setLoading(false);
    }
  };

  const loginWithMicrosoft = () => {
    setLoading(true);
    setErrorMessage(""); // Clear previous errors
    try {
      // This will redirect to your backend OIDC endpoint
      initiateMicrosoftLogin();
    } catch (error) {
      logger.error("Microsoft login error", { error, context: "loginWithMicrosoft" });
      setErrorMessage("Failed to initiate Microsoft authentication");
      setLoading(false);
    }
  };

  const fetchUserInfo = async (): Promise<boolean> => {
    try {
      const response = await checkAuthFunction();
      if (response?.user) {
        setUsername(response.user.email);
        const roleResponse = await getUserOrganization(response.user.id);
        const userRole = roleResponse.role || "";
        setRole(userRole);

        // Set user context in Sentry
        Sentry.setUser({
          email: response.user.email,
          username: response.user.email,
          role: userRole,
          id: response.user.id?.toString(),
        });

        logger.info("User logged in", { username: response.user.email, role: userRole });

        // Check if user has a valid role for the application BEFORE setting authentication
        if (!(ALL_ROLES as readonly string[]).includes(userRole)) {
          setIsAuthenticated(false);
          setUsername("");
          setRole("");
          return false;
        }

        // Only set authentication to true if role validation passes
        setIsAuthenticated(true);
        await syncUserLocalePreferences();
        // If user already accepted privacy, suppress welcome popup
        if (
          typeof response.user.acceptPrivacy === "boolean" &&
          response.user.acceptPrivacy === true
        ) {
          setHasSeenWelcomePopup(true);
          setIsNewLogin(false);
        }
        return true;
      } else {
        setIsAuthenticated(false);
        setUsername("");
        setRole("");
        return false;
      }
    } catch (error) {
      logger.error("Error fetching user info", { error, context: "fetchUserInfo" });
      setIsAuthenticated(false);
      setUsername("");
      setRole("");
      return false;
    }
  };

  const logout = () => {
    // Clear Sentry user context
    Sentry.setUser(null);

    setIsAuthenticated(false);
    setUsername("");
    setRole("");
    setErrorMessage("");
    setHasSeenWelcomePopup(false);
    setIsNewLogin(false);
    // Clear 2FA state
    setRequires2FA(false);
    setPending2FAEmail("");
    // Optional: Make an API call to logout on the server if needed
  };

  const markWelcomePopupAsSeen = () => {
    setHasSeenWelcomePopup(true);
    setIsNewLogin(false);
  };

  return (
    <AuthContext.Provider
      value={{
        isAuthenticated,
        login,
        loginWithMicrosoft,
        logout,
        errorMessage,
        username,
        role,
        loading,
        hasSeenWelcomePopup,
        markWelcomePopupAsSeen,
        isNewLogin,
        requires2FA,
        setRequires2FA,
        pending2FAEmail,
        fetchUserInfo, // Expose fetchUserInfo for 2FA verification
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};
